import filter from "lodash/filter";
import first from "lodash/first";
import { Division, Gender, Lifter, Meet } from "types";
import {
  getDivisionDoc,
  getLifterAwardsWeightClassDoc,
  getPlace,
  getScore,
} from "./lifterHelper";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";
import size from "lodash/size";
import { getGenderOptions, getTeamOptions } from "util/options";
import round from "lodash/round";
import some from "lodash/some";
import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";

type TeamData = {
  team: string;
  points: number;
  lifters: TeamPoints["lifters"];
  gender?: Gender;
};

type TeamDataWithPlace = TeamData & {
  place: number;
};

function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

export const TEAM_SCORING_MAX_LIFTERS_DEFAULT = 5;
export const TEAM_SCORING_FORMULA_DEFAULT = "DOTS_POINTS";
export const TEAM_SCORING_METHOD_DEFAULT = "BY_PLACING";
export const TEAM_SCORING_POINTS_MAP_DEFAULT: Record<number, number> = {
  1: 12,
  2: 9,
  3: 8,
  4: 7,
  5: 6,
  6: 5,
  7: 4,
  8: 3,
  9: 2,
  10: 1,
};
export const TEAM_SCORING_POINTS_GREATER_DEFAULT = 1;

export const getTeamLifters = ({
  meet,
  teamName,
  gender,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
}) => {
  const teamLifters = filter(meet.lifters, { team: teamName });
  if (gender) {
    return filter(teamLifters, { gender });
  }
  return teamLifters;
};

export type TeamPoints = {
  score: number;
  lifters: (
    | Lifter
    | {
        teamPointsContributed: number | null;
        place: number;
        formulaTieBreakerPoints: number;
        division: Division;
        weightClass: string;
      }
  )[];
};

const getPlacingTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  const teamSize =
    meet.teamScoringMaxLifters ?? TEAM_SCORING_MAX_LIFTERS_DEFAULT;
  const teamLifters = getTeamLifters({ meet, teamName, gender });
  const placings = teamLifters.map((lifter) => {
    const lifterDivision = first(lifter.divisions);
    if (!lifterDivision) {
      return null;
    }

    const division = getDivisionDoc(lifterDivision.divisionId, meet);

    if (!division) {
      return null;
    }

    const formulaTieBreakerPoints = getScore({
      lifter,
      division: {
        ...division,
        scoreBy: meet.teamScoringFormula ?? TEAM_SCORING_FORMULA_DEFAULT,
      },
      meet,
      forecasted,
    });

    const place = getPlace(lifter, lifterDivision, meet, forecasted);
    const pointsMap =
      meet.teamScoringPlacingValues ?? TEAM_SCORING_POINTS_MAP_DEFAULT;
    return {
      teamPointsContributed: place
        ? get(
            pointsMap,
            place,
            TEAM_SCORING_POINTS_MAP_DEFAULT[place] ??
              meet.teamScoringPlacingValueOther ??
              TEAM_SCORING_POINTS_GREATER_DEFAULT,
          )
        : null,
      place,
      formulaTieBreakerPoints,
      division,
      ...lifter,
      weightClass: getLifterAwardsWeightClassDoc(meet, lifter, lifterDivision)
        ?.name,
    };
  });

  const successfulPlacings = placings.filter(isNotNull);

  const sortedPlacings = sortBy(successfulPlacings, "place");

  // Point scoring for all World, Continental and Regional Championships shall be: 12, 9, 8, 7, 6, 5, 4, 3, 2 for the first
  // 9 placing in any bodyweight category. Thereafter, each lifter who makes a total in the competition shall be awarded
  // one point. Point scoring for all national competitions shall be at the discretion of the national federation.

  // In the case of a tie for the classification of a team or a nation, the team having
  // the largest number of first places will be ranked first. In the case of a tie between two nations having the same number
  // of first places, the one having the most second places will be classified first, and so on through the placing of the
  // maximum of five scoring lifters. Should teams or Nations finish equally after this procedure has been applied,
  // then the team or Nation with the greater total number of IPF points will be declared the higher placed.

  const withAdjustedPoints = sortedPlacings.map((lifter, index) => {
    if (index < teamSize) {
      return lifter;
    }

    return { ...lifter, teamPointsContributed: 0, formulaTieBreakerPoints: 0 };
  });

  const score = sumBy(withAdjustedPoints, "teamPointsContributed");

  return { score, lifters: withAdjustedPoints };
};

const getFormulaTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  const teamSize = meet.teamScoringMaxLifters ?? 5;
  const teamLifters = getTeamLifters({ meet, teamName, gender });
  const placings = teamLifters.map((lifter) => {
    const lifterDivision = first(lifter.divisions);
    if (!lifterDivision) {
      return null;
    }

    const division = getDivisionDoc(lifterDivision.divisionId, meet);

    if (!division) {
      return null;
    }

    const points = getScore({
      lifter,
      division: {
        ...division,
        scoreBy: meet.teamScoringFormula ?? TEAM_SCORING_FORMULA_DEFAULT,
      },
      meet,
      forecasted,
    });
    const place = getPlace(lifter, lifterDivision, meet, forecasted);

    return {
      teamPointsContributed: points,
      formulaTieBreakerPoints: points,
      place,
      division,
      ...lifter,
      weightClass: getLifterAwardsWeightClassDoc(meet, lifter, lifterDivision)
        ?.name,
    };
  });

  const successfulPlacings = placings.filter(isNotNull);

  const sortedPlacings = sortBy(successfulPlacings, "place");

  const withAdjustedPoints = sortedPlacings.map((lifter, index) => {
    if (index < teamSize) {
      return lifter;
    }

    return { ...lifter, teamPointsContributed: 0, formulaTieBreakerPoints: 0 };
  });

  const score = sumBy(withAdjustedPoints, "teamPointsContributed");

  return { score, lifters: withAdjustedPoints };
};

export const getTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  return meet.teamScoringMethod === "BY_FORMULA"
    ? getFormulaTeamPoints({ meet, teamName, gender, forecasted })
    : getPlacingTeamPoints({ meet, teamName, gender, forecasted });
};

const getPlacingCount = (lifters: any, placing: number) => {
  return size(lifters?.filter((l: any) => l.place === placing));
};

const sortTeams = (a: TeamData, b: TeamData) => {
  if (b.points !== a.points) return b.points - a.points;

  for (let i = 0; i < 100; i++) {
    const aPlacingCount = getPlacingCount(a.lifters, i);
    const bPlacingCount = getPlacingCount(b.lifters, i);
    if (aPlacingCount !== bPlacingCount) {
      return bPlacingCount - aPlacingCount;
    }
  }

  return (
    sumBy(b.lifters, "formulaTieBreakerPoints") -
    sumBy(a.lifters, "formulaTieBreakerPoints")
  );
};

export const getTeamDataForTable = ({
  meet,
  forecasted,
}: {
  meet: Meet;
  forecasted: boolean;
}) => {
  const teams = getTeamOptions(null, meet);
  if (meet.teamScoringDivideByGender !== false) {
    const genders = getGenderOptions(null, meet);
    let byGenderData: (
      | TeamDataWithPlace
      | {
          row: string;
          name?: string;
        }
    )[] = [];
    genders.forEach((gender) => {
      const teamData: TeamData[] = teams.map((team) => {
        const teamName = team.value ?? "";
        const teamPoints = getTeamPoints({
          meet,
          teamName: teamName ?? "",
          gender: gender.value,
          forecasted,
        });
        return {
          team: teamName,
          points: teamPoints.score,
          lifters: teamPoints.lifters,
          gender: gender.value,
        };
      });

      const teamsWithLifters = teamData.filter((t) => {
        return t.lifters.length;
      });
      const sortedTeams = teamsWithLifters.sort(sortTeams);

      const withPlacing = sortedTeams.map((team, index) => {
        return { ...team, place: index + 1, points: round(team.points, 3) };
      });

      if (some(withPlacing, (t) => t.lifters.length)) {
        byGenderData = byGenderData.concat([
          {
            row: "title",
            name:
              gender.value === "MALE"
                ? "Men"
                : gender.value === "FEMALE"
                  ? "Women"
                  : gender.label,
          },
          ...withPlacing,
        ]);
      }
    });

    const data = [{ row: "header" }, ...byGenderData];
    return data;
  }

  const teamData: TeamData[] = teams.map((team) => {
    const teamName = team.value;
    const teamPoints = getTeamPoints({
      meet,
      teamName: teamName ?? "",
      forecasted,
    });
    return {
      team: teamName ?? "",
      points: teamPoints.score,
      lifters: teamPoints.lifters,
    };
  });
  const sortedTeams = teamData.sort(sortTeams);
  const withPlacing = sortedTeams.map((team, index) => {
    return { ...team, place: index + 1, points: round(team.points, 3) };
  });
  const data = [{ row: "header" }, ...withPlacing];
  return data;
};

export const getTeamDataForApi = ({
  meet,
  forecasted,
}: {
  meet: Meet;
  forecasted: boolean;
}): Record<string, { place: number; points: number }> => {
  const tableData = getTeamDataForTable({ meet, forecasted });
  const data = tableData.filter((d): d is TeamDataWithPlace => {
    return !("row" in d);
  });

  let teams: Record<string, TeamDataWithPlace> = {};

  if (meet.teamScoringDivideByGender !== false) {
    teams = keyBy(data, (d) => {
      return `${
        d.gender === "MALE" ? "Men" : d.gender === "FEMALE" ? "Women" : d.gender
      }-${d.team}`;
    });
  } else {
    teams = keyBy(data, (d) => {
      return d.team;
    });
  }

  return mapValues(teams, (t) => {
    return { place: t.place, points: t.points };
  });
};
